import type { GetStaticProps, NextPage } from 'next';
import type { ArticlePreview } from 'interfaces/content/articles/Articles';
import type { Latest } from 'interfaces/LatestPage';
import HubPage from 'pages/Hub/HubPage';
import MostPopularRR from 'components/MostPopularRR';
import getFooter from 'api/Footer/getFooter';
import getMostPopularPosts from 'api/MostPopular/getMostPopular';
import type { HeaderNav } from 'interfaces/navigation/Header';
import handleStaticPropsError from 'utils/handleStaticPropsError';
import NotFoundError from 'interfaces/errors/NotFoundError';
import { setHeadData } from 'utils/metaDataUtils/headData';
import setPageGtmConfig from 'services/Gtm/setPageGtmConfig';
import { setPianoConfig } from 'services/Piano';
import { setarcSpanPageConfig } from 'services/ArcSpan';
import type { ArcSpanPageConfig } from 'services/ArcSpan/entities';
import type { PianoPageConfig } from 'services/Piano/entities/PianoPageConfig';
import getLatest from 'api/Latest/getLatest';
import setSectionPageAdConfig from 'services/Ad/config/setLatestPageAdConfig';
import getMenu from 'api/Header/getMenu';
import { calculatePagePagination, type PagePagination } from 'utils/miscUtils/calculatePageInfo';
import { layoutCount, topicNavBorderColor } from 'constants/constants';
import { PageType } from 'interfaces/content/articles/Post';
import { safe, safeAsync } from 'utils/errorHandlingUtils';
import fallbackData from 'lib/fallbackData/commonData';

interface LatestPageProps {
  mostPopular: ArticlePreview[];
  latest: Latest;
  pageSize: number;
  pagination: PagePagination;
}

const LatestNextPage: NextPage<LatestPageProps> = ({ pagination, mostPopular, latest, pageSize }: LatestPageProps) => (
  <HubPage
    pagination={pagination}
    data={latest}
    type={PageType.LATEST}
    sidebar={[
      mostPopular && (
        <MostPopularRR
          key={1}
          articles={mostPopular}
        />
      ),
    ]}
    infiniteScroll={{ numPostsPerPage: layoutCount.section, slug: 'all', taxonomy: 'latest' }}
    color={topicNavBorderColor.own}
    pageSize={pageSize}
  />
);

export default LatestNextPage;

export const getStaticProps: GetStaticProps = async (context) => {
  const pageSize = layoutCount.theLatest;

  try {
    const { pageNum } = (context.params || {}) as { pageNum: string };

    const currentPageNum = parseInt(pageNum || '1', 10);

    if (Number.isNaN(currentPageNum) || currentPageNum < 1) {
      throw new NotFoundError(`[getHomePageNumber] invalid page number: '${pageNum}'`);
    }

    const latest = await getLatest(pageSize, (currentPageNum - 1) * pageSize);
    const menu: HeaderNav = await safeAsync(
      () => getMenu('LEFT_MENU_COM', 'RIGHT_MENU_COM', 'TOPIC_NAVIGATION_COM', 'own', null),
      fallbackData.headerNav,
    );
    const footer = await safeAsync(() => getFooter(), fallbackData.footer);

    const mostPopular = await safeAsync(() => getMostPopularPosts(), fallbackData.articlesData.posts);

    const dataLayer = safe(() => setPageGtmConfig(latest), fallbackData.dataLayer);
    const pagination = calculatePagePagination(currentPageNum, latest.pageInfo.totalCount, layoutCount.latest, 'all');
    const headData = safe(
      () =>
        setHeadData(
          {
            ...latest,
            canonicalUrl: pageNum ? `${latest.canonicalUrl}page/${pageNum}/` : latest.canonicalUrl,
            posts: latest.posts,
            titlePrefix: pagination.pageInfo,
          } as Latest,
          true,
          'The Latest',
        ),
      fallbackData.headData,
    );
    const pageAdConfig = safe(() => setSectionPageAdConfig(latest), fallbackData.pageAdConfig);
    const arcSpanPageConfig: ArcSpanPageConfig = safe(
      () => setarcSpanPageConfig(latest),
      fallbackData.arcSpanPageConfig,
    );
    const pianoPageConfig: PianoPageConfig = safe(() => setPianoConfig(latest), fallbackData.pianoPageConfig);

    return {
      props: {
        arcSpanPageConfig,
        dataLayer,
        footer,
        headData,
        headerNav: menu,
        latest,
        mostPopular,
        pageAdConfig,
        pageSize,
        pagination,
        pianoPageConfig,
      },
      revalidate: 60,
    };
  } catch (error) {
    return handleStaticPropsError(error as Error);
  }
};
